import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { formatDate, formatDateLong, daysAgo } from '../../utils/format';
import { useToast } from '../ui/Toast';
import Modal from '../shared/Modal';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import {
  CRITICALITIES, CRITICALITY_LABELS, HOSTINGS, HOSTING_LABELS, VERSION_SOURCE_LABELS,
  SUPPORT_STATUS_LABELS, WARN_DAYS, MANUAL_RECHECK_DAYS, deriveSupport, compareVersions,
} from '../../utils/appCatalogueVersions';
import { APPLICATION_CATALOGUE_TOUR_ID, applicationCatalogueTourSteps } from './applicationCatalogueTour';
import PhotoAvatar from '../shared/Avatar';

// ─── Application Catalogue ───────────────────────────────────────────────────
// Replaces the SharePoint Application Wiki. The list is the daily view: every
// application with the version we run, the version the vendor ships and when
// support ends. The application page is the detail view: release cycles from
// endoflife.date, every server it runs on, the patch history and the contacts.
// All the version arithmetic is shared with the server in
// utils/appCatalogueVersions.js, so the page never disagrees with the API.

const inputCls = 'form-input';
const labelCls = 'form-label';
const btnPrimary = 'px-3 py-1.5 text-[10px] font-semibold rounded bg-navy text-white hover:opacity-90 disabled:opacity-50';
const btnGhost = 'px-3 py-1.5 text-[10px] font-semibold rounded bg-white border border-gray-200 text-navy hover:bg-gray-50 disabled:opacity-50';
const btnSmall = 'px-2 py-0.5 text-[9px] font-semibold rounded bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50';
const thCls = 'px-3 py-2 text-left text-[7px] uppercase tracking-wider text-gray-400 font-bold border-b bg-gray-50 whitespace-nowrap';
const tdCls = 'px-3 py-2 text-[10px] text-gray-800';

const STATUS_BADGE = {
  past_eol: 'bg-red-100 text-red-700',
  ending_soon: 'bg-amber-100 text-amber-700',
  supported: 'bg-emerald-100 text-emerald-700',
  vendor_managed: 'bg-emerald-50 text-emerald-700',
  unknown: 'bg-gray-100 text-gray-600',
};
const ROW_TINT = { past_eol: 'bg-red-50', ending_soon: 'bg-amber-50' };
const CRIT_BADGE = { high: 'bg-red-50 text-red-700', medium: 'bg-amber-50 text-amber-700', low: 'bg-gray-100 text-gray-600' };
const ENVIRONMENTS = ['production', 'uat', 'test', 'development', 'dr'];
const ENV_LABELS = { production: 'Production', uat: 'UAT', test: 'Test', development: 'Development', dr: 'DR' };

const today = () => new Date().toISOString().slice(0, 10);
const errorText = (e, fallback) => e?.data?.error || e?.message || fallback;
const personName = (p) => `${p.first_name || ''} ${p.last_name || ''}`.trim();
const initialsOf = (name) => String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';

function Badge({ cls, children, title }) {
  return <span title={title} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold whitespace-nowrap ${cls}`}>{children}</span>;
}

function SupportBadge({ support }) {
  if (!support) return null;
  const { status, days_to_eol: d, support_ends } = support;
  let text = SUPPORT_STATUS_LABELS[status] || status;
  if (status === 'past_eol' && support_ends) text = `Past EOL since ${formatDate(support_ends)}`;
  if (status === 'ending_soon' && d != null) text = d === 0 ? 'Support ends today' : `Support ends in ${d} day${d === 1 ? '' : 's'}`;
  return <Badge cls={STATUS_BADGE[status] || STATUS_BADGE.unknown} title={support_ends ? `Security support ends ${formatDateLong(support_ends)}` : undefined}>{text}</Badge>;
}

function VersionGap({ support }) {
  if (!support?.latest_version || !support.installed_version) return null;
  const behind = support.majors_behind;
  if (behind > 0) return <Badge cls="bg-blue-50 text-blue-700">{behind} major behind</Badge>;
  if (compareVersions(support.installed_version, support.latest_version) < 0) return <Badge cls="bg-gray-100 text-gray-600">Minor behind</Badge>;
  return null;
}

function Avatar({ name, personId }) {
  return <PhotoAvatar as="span" personId={personId} className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-navy text-white text-[7px] font-bold shrink-0">{initialsOf(name)}</PhotoAvatar>;
}

function Owner({ name, personId }) {
  if (!name) return <span className="text-gray-400 italic">Unassigned</span>;
  return <span className="inline-flex items-center gap-1.5"><Avatar name={name} personId={personId} />{name}</span>;
}

function PersonSelect({ people, value, onChange, allowNone = true }) {
  return (
    <select className="form-select" value={value || ''} onChange={e => onChange(e.target.value || null)}>
      {allowNone && <option value="">Unassigned</option>}
      {people.map(p => <option key={p.id} value={p.id}>{personName(p)}</option>)}
    </select>
  );
}

// A business owner or SME is not always one of ours, so the field is free text
// with our own people offered as completions. A name that matches one of ours
// is stored as that person, so they keep receiving the end-of-life alerts;
// anyone else is kept as written and is recorded rather than emailed.
const PEOPLE_LIST_ID = 'app-catalogue-people';

function OwnerInput({ people, personId, external, knownName, onChange, placeholder }) {
  // knownName keeps a stored person readable while the directory loads, and
  // keeps a leaver who is off the list from being blanked out on the next save.
  const value = personId ? (personName(people.find(p => p.id === personId) || {}) || knownName || '') : (external || '');
  const change = (text) => {
    const match = people.find(p => personName(p).toLowerCase() === text.trim().toLowerCase());
    onChange(match ? { id: match.id, external: null } : { id: null, external: text });
  };
  return (
    <>
      <input className={inputCls} value={value} list={PEOPLE_LIST_ID} onChange={e => change(e.target.value)} placeholder={placeholder} />
      <datalist id={PEOPLE_LIST_ID}>{people.map(p => <option key={p.id} value={personName(p)} />)}</datalist>
    </>
  );
}

function Card({ title, action, children, tour }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg" data-tour={tour}>
      {title && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
          <h3 className="text-[10px] font-bold text-navy">{title}</h3>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return <div className={className}><label className={labelCls}>{label}</label>{children}</div>;
}

// ─── Add / edit application ───────────────────────────────────────────────────

const EMPTY_APP = {
  name: '', vendor: '', description: '', criticality: 'medium', hosting: 'self_hosted',
  support_owner_id: null, business_owner_id: null, business_owner_external: '', support_structure: '', update_method: '',
  downtime_note: '', licence: '', installed_version: '', notes: '', resources: [],
};

const resourcesToText = (list) => (list || []).map(r => (r.label && r.label !== r.url ? `${r.label} | ${r.url}` : r.url)).join('\n');
const textToResources = (text) => String(text || '').split('\n').map(l => l.trim()).filter(Boolean).map(l => {
  const [a, b] = l.split('|').map(s => s.trim());
  return b ? { label: a, url: b } : { label: a, url: a };
});

function ApplicationFormDialog({ initial, people, onClose, onSaved }) {
  const { toast } = useToast();
  const editing = !!initial?.id;
  const [form, setForm] = useState(() => ({ ...EMPTY_APP, ...(initial || {}), resources: resourcesToText(initial?.resources) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setError('');
    if (!form.name.trim()) { setError('A name is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, vendor: form.vendor, description: form.description, criticality: form.criticality, hosting: form.hosting,
        support_owner_id: form.support_owner_id || null, business_owner_id: form.business_owner_id || null,
        business_owner_external: form.business_owner_external || null,
        support_structure: form.support_structure, update_method: form.update_method, downtime_note: form.downtime_note,
        licence: form.licence, installed_version: form.installed_version, notes: form.notes, resources: textToResources(form.resources),
      };
      const saved = editing
        ? await api.put(`app-catalogue/applications/${initial.id}`, payload)
        : await api.post('app-catalogue/applications', payload);
      toast.success(editing ? 'Application updated' : 'Application added');
      onSaved(saved);
    } catch (e) {
      setError(errorText(e, 'That did not save.'));
    } finally { setSaving(false); }
  };

  return (
    <Modal title={editing ? `Edit ${initial.name}` : 'Add an application'} onClose={onClose} wide footer={
      <>
        <button onClick={onClose} className={btnGhost}>Cancel</button>
        <button onClick={save} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Add application'}</button>
      </>
    }>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Docker Engine" /></Field>
          <Field label="Vendor"><input className={inputCls} value={form.vendor || ''} onChange={e => set('vendor', e.target.value)} placeholder="Open source (Moby)" /></Field>
        </div>
        <Field label="What it is"><textarea className="form-textarea" rows={2} value={form.description || ''} onChange={e => set('description', e.target.value)} placeholder="One or two sentences on what the application does for us." /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Criticality">
            <select className="form-select" value={form.criticality} onChange={e => set('criticality', e.target.value)}>
              {CRITICALITIES.map(c => <option key={c} value={c}>{CRITICALITY_LABELS[c]}</option>)}
            </select>
          </Field>
          <Field label="Hosting">
            <select className="form-select" value={form.hosting} onChange={e => set('hosting', e.target.value)}>
              {HOSTINGS.map(h => <option key={h} value={h}>{HOSTING_LABELS[h]}</option>)}
            </select>
          </Field>
          <Field label="Installed version (if no servers listed)"><input className={inputCls} value={form.installed_version || ''} onChange={e => set('installed_version', e.target.value)} placeholder="27.4.0" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Alton Valley support primary"><PersonSelect people={people} value={form.support_owner_id} onChange={v => set('support_owner_id', v)} /></Field>
          <Field label="Business owner / SME"><OwnerInput people={people} personId={form.business_owner_id} external={form.business_owner_external} knownName={initial?.business_owner_name} placeholder="Start typing a name, ours or anyone else's" onChange={v => setForm(f => ({ ...f, business_owner_id: v.id, business_owner_external: v.external }))} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Support structure"><input className={inputCls} value={form.support_structure || ''} onChange={e => set('support_structure', e.target.value)} placeholder="Level 2 Alton Valley, Level 3 vendor" /></Field>
          <Field label="Update method"><input className={inputCls} value={form.update_method || ''} onChange={e => set('update_method', e.target.value)} placeholder="Manual, per server" /></Field>
          <Field label="Downtime per patch"><input className={inputCls} value={form.downtime_note || ''} onChange={e => set('downtime_note', e.target.value)} placeholder="About 30 min per server" /></Field>
          <Field label="Licence"><input className={inputCls} value={form.licence || ''} onChange={e => set('licence', e.target.value)} placeholder="Apache 2.0, no renewal" /></Field>
        </div>
        <Field label="Resources (one per line, as Label | URL)"><textarea className="form-textarea" rows={3} value={form.resources} onChange={e => set('resources', e.target.value)} placeholder={'Docker Engine documentation | https://docs.docker.com/engine/\nRelease notes | https://docs.docker.com/engine/release-notes/'} /></Field>
        <Field label="Notes"><textarea className="form-textarea" rows={3} value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Anything the next person patching it should know." /></Field>
        {!editing && <p className="text-[9px] text-gray-500">Version tracking (the endoflife.date product, or hand-entered latest version and support end) is set on the application page once it is added.</p>}
        {error && <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>}
      </div>
    </Modal>
  );
}

// ─── Version tracking ─────────────────────────────────────────────────────────

function VersionTrackingDialog({ app, onClose, onSaved }) {
  const { toast } = useToast();
  const [source, setSource] = useState(app.version_source || 'manual');
  const [product, setProduct] = useState(app.version_source_ref || '');
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [manualLatest, setManualLatest] = useState(app.manual_latest_version || '');
  const [manualEnds, setManualEnds] = useState(app.manual_support_ends || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (source !== 'endoflife' || query.trim().length < 2) { setMatches([]); return; }
    let live = true;
    setSearching(true);
    const t = setTimeout(() => {
      api.get('app-catalogue/products', { q: query.trim() })
        .then(r => { if (live) setMatches(Array.isArray(r) ? r : []); })
        .catch(() => { if (live) setMatches([]); })
        .finally(() => { if (live) setSearching(false); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [query, source]);

  const save = async ({ checkNow = false } = {}) => {
    setError('');
    if (source === 'endoflife' && !product.trim()) { setError('Choose the endoflife.date product to track.'); return; }
    setSaving(true);
    try {
      const saved = await api.put(`app-catalogue/applications/${app.id}`, {
        version_source: source, version_source_ref: source === 'endoflife' ? product.trim() : null,
        manual_latest_version: source === 'manual' ? manualLatest : app.manual_latest_version,
        manual_support_ends: source === 'manual' ? (manualEnds || null) : app.manual_support_ends,
        recheck_manual: source === 'manual',
      });
      let checked = null;
      if (source === 'endoflife' && checkNow) {
        checked = await api.post(`app-catalogue/applications/${app.id}/check`, {});
        if (checked?.ok === false) { setError(`Saved, but the check failed: ${checked.error}`); onSaved(saved, { keepOpen: true }); return; }
        toast.success(`Checked: latest release ${checked.latest_version || 'unknown'}`);
      } else {
        toast.success('Version tracking saved');
      }
      onSaved(saved);
    } catch (e) {
      setError(errorText(e, 'That did not save.'));
    } finally { setSaving(false); }
  };

  const Option = ({ value, title, children }) => (
    <label className={`block border rounded-lg px-3 py-2 cursor-pointer ${source === value ? 'border-gold bg-gold/5' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start gap-2">
        <input type="radio" name="version-source" className="mt-0.5" checked={source === value} onChange={() => setSource(value)} />
        <div className="flex-1">
          <div className="text-[10px] font-semibold text-navy">{title}</div>
          {children}
        </div>
      </div>
    </label>
  );

  return (
    <Modal title="Version tracking" onClose={onClose} wide footer={
      <>
        <button onClick={onClose} className={btnGhost}>Cancel</button>
        {source === 'endoflife' && <button onClick={() => save({ checkNow: true })} disabled={saving} className={btnGhost}>{saving ? 'Working…' : 'Save and check now'}</button>}
        <button onClick={() => save()} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
      </>
    }>
      <div className="space-y-3">
        <p className="text-[9px] text-gray-500">{app.name}. Where the latest release and end-of-life dates come from.</p>
        <Option value="endoflife" title="endoflife.date">
          <p className="text-[8px] text-gray-500">Release cycles, latest patch and support end dates for a few hundred products. Refreshed nightly at 06:10.</p>
          {source === 'endoflife' && (
            <div className="mt-2 space-y-1.5">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Product">
                  <input className={inputCls} value={product} onChange={e => setProduct(e.target.value)} placeholder="docker-engine" />
                </Field>
                <Field label="Search the catalogue">
                  <input className={inputCls} value={query} onChange={e => setQuery(e.target.value)} placeholder="Type to search, e.g. grafana" />
                </Field>
              </div>
              {(matches.length > 0 || searching) && (
                <div className="flex flex-wrap gap-1">
                  {searching && <span className="text-[8px] text-gray-400">Searching…</span>}
                  {matches.map(m => (
                    <button key={m} type="button" onClick={() => { setProduct(m); setQuery(''); }} className={`${btnSmall} ${m === product ? 'border-gold text-navy' : ''}`}>{m}</button>
                  ))}
                </div>
              )}
              {app.last_checked_at && (
                <p className="text-[8px] text-gray-500">Last check {formatDate(app.last_checked_at)}: latest {app.latest_version || 'unknown'}{app.last_check_error ? `. Last error: ${app.last_check_error}` : ''}.</p>
              )}
            </div>
          )}
        </Option>
        <Option value="manual" title="Entered by hand">
          <p className="text-[8px] text-gray-500">For vendors with no public feed. The support owner is reminded to re-check every {MANUAL_RECHECK_DAYS} days.</p>
          {source === 'manual' && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Field label="Latest release"><input className={inputCls} value={manualLatest} onChange={e => setManualLatest(e.target.value)} placeholder="3.6" /></Field>
              <Field label="Security support ends"><input type="date" className={inputCls} value={manualEnds} onChange={e => setManualEnds(e.target.value)} /></Field>
            </div>
          )}
        </Option>
        <div>
          <div className={labelCls}>End-of-life alerts</div>
          <div className="flex flex-wrap gap-1 items-center">
            {['90 days before', '30 days before', 'On the day'].map(t => <Badge key={t} cls="bg-blue-50 text-blue-700">{t}</Badge>)}
            <Badge cls="bg-gray-100 text-gray-600">Weekly while past end of life</Badge>
          </div>
          <p className="text-[8px] text-gray-500 mt-1">Sent as an ops notification and by email to the support primary and business owner.</p>
        </div>
        {error && <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>}
      </div>
    </Modal>
  );
}

// ─── Instances ────────────────────────────────────────────────────────────────

function InstanceDialog({ app, initial, people, onClose, onSaved }) {
  const { toast } = useToast();
  const editing = !!initial?.id;
  const [form, setForm] = useState(() => ({
    server: initial?.server || '', environment: initial?.environment || 'production', client: initial?.client || '',
    installed_version: initial?.installed_version || app.installed_version || '', last_patched_on: initial?.last_patched_on || '',
    last_patched_by: initial?.last_patched_by || null, notes: initial?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setError('');
    if (!form.server.trim()) { setError('A server name is required.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, last_patched_on: form.last_patched_on || null, last_patched_by: form.last_patched_by || null };
      const saved = editing
        ? await api.put(`app-catalogue/applications/${app.id}/instances/${initial.id}`, payload)
        : await api.post(`app-catalogue/applications/${app.id}/instances`, payload);
      toast.success(editing ? 'Instance updated' : 'Instance added');
      onSaved(saved);
    } catch (e) { setError(errorText(e, 'That did not save.')); } finally { setSaving(false); }
  };

  return (
    <Modal title={editing ? `Edit ${initial.server}` : 'Add an instance'} onClose={onClose} footer={
      <>
        <button onClick={onClose} className={btnGhost}>Cancel</button>
        <button onClick={save} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Add instance'}</button>
      </>
    }>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Server"><input className={inputCls} value={form.server} onChange={e => set('server', e.target.value)} placeholder="AV-DOCK-01" /></Field>
          <Field label="Environment">
            <select className="form-select" value={form.environment} onChange={e => set('environment', e.target.value)}>
              {ENVIRONMENTS.map(x => <option key={x} value={x}>{ENV_LABELS[x]}</option>)}
            </select>
          </Field>
          <Field label="Client (if hosted for one)"><input className={inputCls} value={form.client} onChange={e => set('client', e.target.value)} placeholder="Internal" /></Field>
          <Field label="Installed version"><input className={inputCls} value={form.installed_version} onChange={e => set('installed_version', e.target.value)} placeholder="27.4.0" /></Field>
          <Field label="Last patched on"><input type="date" className={inputCls} value={form.last_patched_on} onChange={e => set('last_patched_on', e.target.value)} /></Field>
          <Field label="Last patched by"><PersonSelect people={people} value={form.last_patched_by} onChange={v => set('last_patched_by', v)} /></Field>
        </div>
        <Field label="Notes"><input className={inputCls} value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>
        {error && <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>}
      </div>
    </Modal>
  );
}

// ─── Log a patch ──────────────────────────────────────────────────────────────

function LogPatchDialog({ app, people, currentUser, onClose, onSaved }) {
  const { toast } = useToast();
  const instances = app.instances || [];
  const [toVersion, setToVersion] = useState(app.support?.latest_version || '');
  const [ticked, setTicked] = useState(() => new Set(instances.map(i => i.id)));
  // Every instance starts ticked, including one that arrives while the dialog
  // is open (the page reloads after an instance is added); untick to leave one out.
  const [seen, setSeen] = useState(() => new Set(instances.map(i => i.id)));
  useEffect(() => {
    const fresh = instances.filter(i => !seen.has(i.id));
    if (!fresh.length) return;
    setSeen(s => new Set([...s, ...fresh.map(i => i.id)]));
    setTicked(s => new Set([...s, ...fresh.map(i => i.id)]));
  }, [instances, seen]);
  const [appliedOn, setAppliedOn] = useState(today());
  const [appliedBy, setAppliedBy] = useState(currentUser?.id || null);
  const [downtime, setDowntime] = useState(app.downtime_note || '');
  const [changeRef, setChangeRef] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (id) => setTicked(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const unticked = instances.filter(i => !ticked.has(i.id));

  // What the version being logged would mean, by the same rule the server uses.
  const preview = useMemo(() => deriveSupport({ ...app, installed_version: toVersion, cycles: app.cycles || [] }), [app, toVersion]);
  const versionNote = !toVersion ? null
    : preview.status === 'supported' ? { cls: 'text-emerald-700', text: preview.latest_version && compareVersions(toVersion, preview.latest_version) >= 0 ? 'Latest release, supported' : 'Supported' }
    : preview.status === 'ending_soon' ? { cls: 'text-amber-700', text: `Supported, but security support ends ${formatDate(preview.support_ends)}` }
    : preview.status === 'past_eol' ? { cls: 'text-red-700', text: `Already past end of life (${formatDate(preview.support_ends)})` }
    : null;

  const save = async () => {
    setError('');
    if (!toVersion.trim()) { setError('Enter the version that was installed.'); return; }
    if (instances.length && ticked.size === 0) { setError('Tick at least one instance the patch was applied to.'); return; }
    setSaving(true);
    try {
      await api.post(`app-catalogue/applications/${app.id}/patches`, {
        to_version: toVersion.trim(), applied_on: appliedOn, applied_by_id: appliedBy, instance_ids: [...ticked],
        downtime_note: downtime, change_reference: changeRef, notes,
      });
      toast.success(`Patch to ${toVersion.trim()} logged`);
      onSaved();
    } catch (e) { setError(errorText(e, 'That did not save.')); } finally { setSaving(false); }
  };

  const count = instances.length ? ticked.size : null;
  return (
    <Modal title="Log a patch" onClose={onClose} wide footer={
      <>
        <button onClick={onClose} className={btnGhost}>Cancel</button>
        <button onClick={save} disabled={saving} className={btnPrimary}>{saving ? 'Logging…' : count == null ? 'Log patch' : `Log patch on ${count} instance${count === 1 ? '' : 's'}`}</button>
      </>
    }>
      <div className="space-y-3">
        <p className="text-[9px] text-gray-500">{app.name}. Recording a patch updates the installed version on the instances you tick.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Version installed">
            <input className={inputCls} value={toVersion} onChange={e => setToVersion(e.target.value)} placeholder={app.support?.latest_version || '29.8.0'} />
            {versionNote && <div className={`text-[8px] mt-0.5 ${versionNote.cls}`}>{versionNote.text}</div>}
          </Field>
          <Field label="Date applied"><input type="date" className={inputCls} value={appliedOn} onChange={e => setAppliedOn(e.target.value)} /></Field>
        </div>
        {instances.length > 0 && (
          <div>
            <div className={labelCls}>Instances patched</div>
            <div className="border border-gray-200 rounded overflow-hidden">
              {instances.map(i => (
                <label key={i.id} className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 last:border-b-0 text-[10px] cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={ticked.has(i.id)} onChange={() => toggle(i.id)} />
                  <span className="font-semibold text-navy w-32 truncate">{i.server}</span>
                  <span className="text-gray-500 w-20">{ENV_LABELS[i.environment] || i.environment}</span>
                  <span className="text-gray-500">{ticked.has(i.id) ? `${i.installed_version || 'unknown'} to ${toVersion || '…'}` : `Stays on ${i.installed_version || 'unknown'}`}</span>
                </label>
              ))}
            </div>
            {unticked.length > 0 && ticked.size > 0 && (
              <div className="mt-1.5 text-[9px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                {unticked.map(i => i.server).join(', ')} {unticked.length === 1 ? 'stays' : 'stay'} on {[...new Set(unticked.map(i => i.installed_version || 'unknown'))].join(' / ')}, so {app.name} keeps its current support status until {unticked.length === 1 ? 'it is' : 'they are'} patched too. Log {unticked.length === 1 ? 'it' : 'them'} separately when done.
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Applied by"><PersonSelect people={people} value={appliedBy} onChange={setAppliedBy} allowNone={false} /></Field>
          <Field label="Downtime taken"><input className={inputCls} value={downtime} onChange={e => setDowntime(e.target.value)} placeholder="30 min per server" /></Field>
          <Field label="Change reference"><input className={inputCls} value={changeRef} onChange={e => setChangeRef(e.target.value)} placeholder="CHG-0498" /></Field>
        </div>
        <Field label="Notes"><textarea className="form-textarea" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="What was done, in what order, and anything the next person should know." /></Field>
        {error && <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>}
      </div>
    </Modal>
  );
}

// ─── Application page ─────────────────────────────────────────────────────────

function Stat({ label, value, sub, cls = 'text-navy' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
      <div className="text-[7px] font-bold uppercase tracking-wider text-gray-400">{label}</div>
      <div className={`text-[16px] font-extrabold leading-tight ${cls}`}>{value}</div>
      {sub && <div className="text-[8px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((new Date() - new Date(`${String(iso).slice(0, 10)}T00:00:00`)) / 86400000);
}

function ApplicationDetail({ id, people, currentUser, onBack, onChanged }) {
  const { toast } = useToast();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // 'edit' | 'versions' | 'instance' | 'patch'
  const [instanceEditing, setInstanceEditing] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`app-catalogue/applications/${id}`).then(setApp).catch(() => setApp(null)).finally(() => setLoading(false));
  }, [id]);
  useEffect(load, [load]);

  const changed = () => { load(); onChanged?.(); };

  const checkNow = async () => {
    setChecking(true);
    try {
      const r = await api.post(`app-catalogue/applications/${id}/check`, {});
      if (r?.ok === false) toast.error(`Check failed: ${r.error}`);
      else toast.success(`Checked endoflife.date: latest release ${r.latest_version || 'unknown'}`);
      changed();
    } catch (e) { toast.error(errorText(e, 'The check did not run.')); } finally { setChecking(false); }
  };

  const removeInstance = async (inst) => {
    if (!window.confirm(`Remove ${inst.server} from ${app.name}? The patch history keeps its entries.`)) return;
    try { await api.delete(`app-catalogue/applications/${id}/instances/${inst.id}`); toast.success('Instance removed'); changed(); }
    catch (e) { toast.error(errorText(e, 'Could not remove it.')); }
  };

  if (loading && !app) return <div className="text-center py-16 text-[10px] text-gray-400">Loading…</div>;
  if (!app) return <div className="text-center py-16 text-[10px] text-gray-400">Application not found. <button onClick={onBack} className="text-navy underline">Back to the catalogue</button></div>;

  const s = app.support || {};
  const manualStale = app.version_source === 'manual' && (!app.manual_checked_at || daysSince(app.manual_checked_at) > MANUAL_RECHECK_DAYS);
  const recentAlerts = (app.alerts || []).slice(0, 3);
  const lastPatch = (app.patches || [])[0];
  // Who the end-of-life banner is about: every instance when they all share
  // the version, otherwise the ones still on the version that has lapsed.
  const eolSubject = (() => {
    const n = app.instance_count || 0;
    if (!n) return `Installed version ${app.installed_version}`;
    const lagging = (app.instances || []).filter(i => (i.installed_version || '') === app.installed_version);
    if (lagging.length === n) return `All ${n} instance${n === 1 ? ' runs' : 's run'} ${app.installed_version}`;
    return `${lagging.length} of ${n} instances (${lagging.map(i => i.server).join(', ')}) still ${lagging.length === 1 ? 'runs' : 'run'} ${app.installed_version}`;
  })();

  return (
    <div className="space-y-3">
      <button onClick={onBack} className="text-[10px] font-semibold text-gray-500 hover:text-navy">← Application Catalogue</button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-navy text-[14px] font-extrabold shrink-0">{initialsOf(app.name)}</div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-navy leading-tight">{app.name}</h1>
              <Badge cls={CRIT_BADGE[app.criticality]}>{CRITICALITY_LABELS[app.criticality]} criticality</Badge>
              <SupportBadge support={s} />
              {app.status === 'retired' && <Badge cls="bg-gray-100 text-gray-600">Retired</Badge>}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">{[app.vendor, app.description].filter(Boolean).join('. ')}</div>
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {app.version_source === 'endoflife' && <button onClick={checkNow} disabled={checking} className={btnGhost}>{checking ? 'Checking…' : 'Check version now'}</button>}
          <button onClick={() => setDialog('edit')} className={btnGhost}>Edit</button>
          <button onClick={() => setDialog('patch')} className={btnPrimary}>Log a patch</button>
        </div>
      </div>

      {(s.status === 'past_eol' || s.status === 'ending_soon') && (
        <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${s.status === 'past_eol' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex-1">
            <div className={`text-[11px] font-bold ${s.status === 'past_eol' ? 'text-red-800' : 'text-amber-800'}`}>
              {s.status === 'past_eol'
                ? `${eolSubject}, which stopped receiving security fixes on ${formatDateLong(s.support_ends)}`
                : `Security support for ${app.installed_version} ends in ${s.days_to_eol} day${s.days_to_eol === 1 ? '' : 's'}, on ${formatDateLong(s.support_ends)}`}
            </div>
            <div className={`text-[9px] mt-0.5 ${s.status === 'past_eol' ? 'text-red-700' : 'text-amber-700'}`}>
              {s.latest_version ? `Latest supported release is ${s.latest_version}. ` : ''}
              {app.downtime_note ? `Downtime: ${app.downtime_note}. ` : ''}
              {recentAlerts.length ? `Last alerted ${formatDate(recentAlerts[0].sent_at)} (${[...new Set(recentAlerts.map(a => a.person_name).filter(Boolean))].join(', ')}).` : 'No alerts sent yet; owners are told 90, 30 and 0 days before support ends.'}
            </div>
          </div>
        </div>
      )}
      {manualStale && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[9px] text-amber-800">
          This application is maintained by hand and {app.manual_checked_at ? `was last checked ${formatDate(app.manual_checked_at)}` : 'has never been checked'} against the vendor. <button onClick={() => setDialog('versions')} className="underline font-semibold">Re-check the latest version and support end</button>.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-start">
        <div className="lg:col-span-2 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Installed" value={app.installed_version || '—'} sub={app.instance_count ? (app.instance_versions.length === 1 ? `Same on all ${app.instance_count} instance${app.instance_count === 1 ? '' : 's'}` : `${app.instance_versions.length} different versions across ${app.instance_count} instances`) : 'No instances listed'} />
            <Stat label="Latest release" value={s.latest_version || '—'} sub={app.version_source === 'endoflife' ? (app.last_checked_at ? `Checked ${daysAgo(app.last_checked_at)}` : 'Not checked yet') : (app.manual_checked_at ? `Entered by hand, checked ${daysAgo(app.manual_checked_at)}` : 'Entered by hand')} cls={s.majors_behind > 0 ? 'text-blue-700' : 'text-navy'} />
            <Stat label="Last patched" value={app.last_patched_on ? formatDate(app.last_patched_on) : '—'} sub={app.last_patched_on ? `${daysAgo(app.last_patched_on)}${lastPatch?.applied_by_name ? `, by ${lastPatch.applied_by_name}` : ''}` : 'Nothing logged yet'} />
          </div>

          {app.version_source === 'endoflife' && (
            <Card title="Release cycles" action={<span className="text-[8px] text-gray-500">Source: endoflife.date, product {app.version_source_ref}. Refreshed nightly.</span>}>
              {app.cycles?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr>{['Cycle', 'Latest patch', 'Released', 'Security support ends', 'Status'].map(h => <th key={h} className={thCls}>{h}</th>)}</tr></thead>
                    <tbody>
                      {app.cycles.slice(0, 8).map(c => {
                        const mine = String(c.cycle) === s.installed_cycle;
                        const eolDays = typeof c.eol === 'string' ? Math.round((new Date(`${c.eol}T00:00:00`) - new Date(`${today()}T00:00:00`)) / 86400000) : null;
                        const status = c.eol === true || (eolDays != null && eolDays <= 0) ? 'past_eol' : eolDays != null && eolDays <= WARN_DAYS ? 'ending_soon' : 'supported';
                        return (
                          <tr key={c.cycle} className={`border-b border-gray-100 ${mine && status === 'past_eol' ? 'bg-red-50' : ''}`}>
                            <td className={`${tdCls} font-semibold text-navy`}>{c.cycle} {mine && <Badge cls="bg-gray-100 text-gray-600">Installed</Badge>}</td>
                            <td className={tdCls}>{c.latest || '—'}</td>
                            <td className={tdCls}>{c.releaseDate ? formatDate(c.releaseDate) : '—'}</td>
                            <td className={tdCls}>{typeof c.eol === 'string' ? formatDate(c.eol) : c.eol === true ? 'Ended' : <span className="text-gray-400">Not yet announced</span>}</td>
                            <td className={tdCls}><Badge cls={STATUS_BADGE[status]}>{status === 'ending_soon' ? `Support ends in ${eolDays} days` : SUPPORT_STATUS_LABELS[status]}</Badge></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-3 py-4 text-[10px] text-gray-500">{app.last_check_error ? `The last check failed: ${app.last_check_error}` : 'Not checked yet.'} <button onClick={checkNow} className="text-navy underline font-semibold">Check now</button>.</div>
              )}
            </Card>
          )}

          <Card title="Instances" action={<button onClick={() => { setInstanceEditing(null); setDialog('instance'); }} className={btnSmall}>Add instance</button>}>
            {app.instances?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr>{['Server', 'Environment', 'Client', 'Version', 'Last patched', 'By', ''].map((h, i) => <th key={i} className={thCls}>{h}</th>)}</tr></thead>
                  <tbody>
                    {app.instances.map(i => {
                      const mine = deriveSupport({ ...app, installed_version: i.installed_version, cycles: app.cycles || [] });
                      return (
                        <tr key={i.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className={`${tdCls} font-semibold text-navy`}>{i.server}</td>
                          <td className={tdCls}>{ENV_LABELS[i.environment] || i.environment}</td>
                          <td className={tdCls}>{i.client || <span className="text-gray-400">Internal</span>}</td>
                          <td className={tdCls}>{i.installed_version || '—'} {mine.status === 'past_eol' && <Badge cls={STATUS_BADGE.past_eol}>EOL</Badge>}{mine.status === 'ending_soon' && <Badge cls={STATUS_BADGE.ending_soon}>{mine.days_to_eol}d</Badge>}</td>
                          <td className={tdCls}>{i.last_patched_on ? formatDate(i.last_patched_on) : <span className="text-gray-400">Never logged</span>}</td>
                          <td className={tdCls}>{i.last_patched_by_name || '—'}</td>
                          <td className={`${tdCls} text-right whitespace-nowrap`}>
                            <button onClick={() => { setInstanceEditing(i); setDialog('instance'); }} className={btnSmall}>Edit</button>{' '}
                            <button onClick={() => removeInstance(i)} className={btnSmall}>Remove</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-3 py-4 text-[10px] text-gray-500">No servers listed yet. Add each server this runs on so patches can be tracked per instance{app.installed_version ? `; until then the installed version is ${app.installed_version}` : ''}.</div>
            )}
          </Card>

          <Card title="Patch history" action={<span className="text-[8px] text-gray-500">Every logged patch, newest first</span>}>
            {app.patches?.length ? (
              <div className="px-3 py-2">
                {app.patches.map((p, idx) => (
                  <div key={p.id} className="grid gap-x-3" style={{ gridTemplateColumns: '80px 14px minmax(0, 1fr)' }}>
                    <div className="text-[9px] text-gray-500 text-right pt-0.5">{formatDate(p.applied_on)}</div>
                    <div className="flex flex-col items-center"><span className="w-2 h-2 rounded-full bg-navy mt-1" />{idx < app.patches.length - 1 && <span className="flex-1 w-px bg-gray-200" />}</div>
                    <div className="pb-3">
                      <div className="text-[10px] font-semibold text-navy">{p.from_version ? <>{p.from_version} <span className="text-gray-400 font-normal">to</span> </> : null}{p.to_version} <span className="text-gray-500 font-normal">{p.instance_count ? `on ${p.instance_count} instance${p.instance_count === 1 ? '' : 's'}` : ''}</span></div>
                      <div className="text-[9px] text-gray-500">{[p.applied_by_name, p.downtime_note ? `Downtime ${p.downtime_note}` : null, p.change_reference ? `Change ${p.change_reference}` : null].filter(Boolean).join('. ')}</div>
                      {p.notes && <div className="text-[9px] text-gray-800 mt-0.5 whitespace-pre-line">{p.notes}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-3 py-4 text-[10px] text-gray-500">No patches logged yet. Use Log a patch once one is applied.</div>
            )}
          </Card>
        </div>

        <div className="space-y-3">
          <Card title="Contacts">
            <div className="px-3 py-2 space-y-2">
              <div><div className={labelCls}>Alton Valley support primary</div><div className="text-[10px] text-gray-800"><Owner name={app.support_owner_name} personId={app.support_owner_id} /></div></div>
              <div><div className={labelCls}>Business owner / SME</div><div className="text-[10px] text-gray-800 flex items-center gap-1.5"><Owner name={app.business_owner_name} personId={app.business_owner_id} />{app.business_owner_external && <Badge cls="bg-gray-100 text-gray-600">Outside Alton Valley</Badge>}</div></div>
              <p className="text-[8px] text-gray-500">{app.business_owner_external
                ? 'The end-of-life alerts go to the Alton Valley support primary. We hold no address for an owner outside Alton Valley, so they are recorded here rather than emailed.'
                : 'Both receive the end-of-life alerts.'}</p>
            </div>
          </Card>
          <Card title="Support">
            <div className="px-3 py-2 grid grid-cols-2 gap-2 text-[10px] text-gray-800">
              <div><div className={labelCls}>Support structure</div>{app.support_structure || <span className="text-gray-400">—</span>}</div>
              <div><div className={labelCls}>Update method</div>{app.update_method || <span className="text-gray-400">—</span>}</div>
              <div><div className={labelCls}>Downtime per patch</div>{app.downtime_note || <span className="text-gray-400">—</span>}</div>
              <div><div className={labelCls}>Licence</div>{app.licence || <span className="text-gray-400">—</span>}</div>
              <div><div className={labelCls}>Hosting</div>{HOSTING_LABELS[app.hosting] || app.hosting}</div>
            </div>
          </Card>
          <Card title="Version tracking" action={<button onClick={() => setDialog('versions')} className={btnSmall}>Configure</button>}>
            <div className="px-3 py-2 space-y-1 text-[10px] text-gray-800">
              <div className="flex justify-between gap-2"><span className="text-gray-500">Source</span><span>{VERSION_SOURCE_LABELS[app.version_source] || app.version_source}</span></div>
              {app.version_source === 'endoflife' && <div className="flex justify-between gap-2"><span className="text-gray-500">Product</span><span>{app.version_source_ref}</span></div>}
              <div className="flex justify-between gap-2"><span className="text-gray-500">Last check</span><span>{app.version_source === 'endoflife' ? (app.last_checked_at ? formatDate(app.last_checked_at) : 'Never') : (app.manual_checked_at ? formatDate(app.manual_checked_at) : 'Never')}</span></div>
              {app.last_check_error && <div className="text-[9px] text-red-700">Last error: {app.last_check_error}</div>}
              <div className="flex justify-between gap-2"><span className="text-gray-500">Alerts</span><span className="text-right">90, 30 and 0 days before support ends, then weekly</span></div>
            </div>
          </Card>
          {app.resources?.length > 0 && (
            <Card title="Resources">
              <div className="px-3 py-2 space-y-1 text-[10px]">
                {app.resources.map((r, i) => <div key={i}><a href={r.url} target="_blank" rel="noreferrer" className="text-gold hover:text-navy underline">{r.label || r.url}</a></div>)}
              </div>
            </Card>
          )}
          {app.notes && (
            <Card title="Notes">
              <div className="px-3 py-2 text-[10px] text-gray-800 whitespace-pre-line">{app.notes}</div>
            </Card>
          )}
          {app.alerts?.length > 0 && (
            <Card title="Alerts sent">
              <div className="px-3 py-2 space-y-1">
                {app.alerts.slice(0, 6).map(a => (
                  <div key={a.id} className="text-[9px] text-gray-800"><span className="text-gray-500">{formatDate(a.sent_at)}</span> {a.person_name || 'Owner'}: {a.message}</div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {dialog === 'edit' && <ApplicationFormDialog initial={app} people={people} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); changed(); }} />}
      {dialog === 'versions' && <VersionTrackingDialog app={app} onClose={() => setDialog(null)} onSaved={(_, opts) => { if (!opts?.keepOpen) setDialog(null); changed(); }} />}
      {dialog === 'instance' && <InstanceDialog app={app} initial={instanceEditing} people={people} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); changed(); }} />}
      {dialog === 'patch' && <LogPatchDialog app={app} people={people} currentUser={currentUser} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); changed(); }} />}
    </div>
  );
}

// ─── The catalogue list ───────────────────────────────────────────────────────

const STATUS_ORDER = { past_eol: 0, ending_soon: 1, unknown: 2, supported: 3, vendor_managed: 4 };

export default function ApplicationCataloguePage({ initialApplicationId = null }) {
  const { currentUser, navigate } = useApp();
  const { toast } = useToast();
  const [apps, setApps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(initialApplicationId);
  const [search, setSearch] = useState('');
  const [critFilter, setCritFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [adding, setAdding] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get('app-catalogue/applications')
      .then(r => { setApps(r?.applications || []); setSummary(r?.summary || null); })
      .catch(e => toast.error(errorText(e, 'Could not load the catalogue.')))
      .finally(() => setLoading(false));
  }, [toast]);
  useEffect(load, [load]);
  useEffect(() => { api.get('people').then(p => setPeople(Array.isArray(p) ? p : [])).catch(() => {}); }, []);
  useEffect(() => { setSelectedId(initialApplicationId); }, [initialApplicationId]);

  const tour = useFeatureTour(APPLICATION_CATALOGUE_TOUR_ID, { enabled: !loading && !selectedId });

  const open = (id) => { setSelectedId(id); navigate('application-catalogue', id); };
  const back = () => { setSelectedId(null); navigate('application-catalogue', null); load(); };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('app-catalogue/run', {});
      toast.success(`Checked ${r.checked} application${r.checked === 1 ? '' : 's'}${r.failed ? `, ${r.failed} failed` : ''}${r.alerts ? `, ${r.alerts} alert${r.alerts === 1 ? '' : 's'} sent` : ''}`);
      load();
    } catch (e) { toast.error(errorText(e, 'The check did not run.')); } finally { setRunning(false); }
  };

  const owners = useMemo(() => {
    const m = new Map();
    for (const a of apps) {
      if (a.support_owner_id) m.set(a.support_owner_id, a.support_owner_name);
      if (a.business_owner_id) m.set(a.business_owner_id, a.business_owner_name);
      else if (a.business_owner_external) m.set(`ext:${a.business_owner_external}`, a.business_owner_external);
    }
    return [...m.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [apps]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return apps
      .filter(a => a.status === 'active')
      .filter(a => !q || [a.name, a.vendor, a.support_owner_name, a.business_owner_name].some(v => String(v || '').toLowerCase().includes(q)))
      .filter(a => critFilter === 'all' || a.criticality === critFilter)
      .filter(a => statusFilter === 'all' || a.support?.status === statusFilter)
      .filter(a => ownerFilter === 'all' || a.support_owner_id === ownerFilter || a.business_owner_id === ownerFilter
        || (a.business_owner_external && `ext:${a.business_owner_external}` === ownerFilter))
      .sort((a, b) => (STATUS_ORDER[a.support?.status] ?? 9) - (STATUS_ORDER[b.support?.status] ?? 9) || a.name.localeCompare(b.name));
  }, [apps, search, critFilter, statusFilter, ownerFilter]);

  if (selectedId) {
    return (
      <div className="p-4">
        <ApplicationDetail id={selectedId} people={people} currentUser={currentUser} onBack={back} onChanged={load} />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap" data-tour="page-header">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-navy">Application Catalogue</h1>
            <button onClick={tour.start} className="w-[23px] h-[23px] rounded-full border border-gray-200 text-gray-500 hover:text-navy hover:border-navy text-[11px] font-bold" aria-label="Replay the guided tour of this page" title="Replay the guided tour of this page">?</button>
          </div>
          <p className="text-[10px] text-gray-500 mt-0.5">Every application Alton Valley supports: the version we run, the version the vendor ships, and when support runs out. Latest versions refresh nightly at 06:10.</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={runNow} disabled={running} className={btnGhost}>{running ? 'Checking…' : 'Check all versions now'}</button>
          <button onClick={() => setAdding(true)} className={btnPrimary} data-tour="add-application">Add application</button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-tour="summary-tiles">
          <Stat label="Applications tracked" value={summary.tracked} />
          <Stat label="Up to date" value={summary.up_to_date} cls="text-emerald-700" />
          <Stat label="Behind latest" value={summary.behind} cls="text-blue-700" />
          <Stat label="Support ends within 90 days" value={summary.ending_soon} cls="text-amber-700" />
          <Stat label="Past end of life" value={summary.past_eol} cls="text-red-700" />
          <Stat label="Position unknown" value={summary.unknown} cls="text-gray-500" />
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center" data-tour="filters">
        <input className="form-input w-56" placeholder="Search applications, vendors, owners" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-select w-36" value={critFilter} onChange={e => setCritFilter(e.target.value)}>
          <option value="all">All criticality</option>
          {CRITICALITIES.map(c => <option key={c} value={c}>{CRITICALITY_LABELS[c]}</option>)}
        </select>
        <select className="form-select w-44" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All support statuses</option>
          {Object.entries(SUPPORT_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="form-select w-40" value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
          <option value="all">All owners</option>
          {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <span className="text-[9px] text-gray-500 ml-auto">Showing {visible.length} of {apps.filter(a => a.status === 'active').length}</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-tour="catalogue-table">
        {loading ? (
          <div className="text-center py-16 text-[10px] text-gray-400">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 text-[10px] text-gray-400">
            {apps.length === 0 ? 'Nothing in the catalogue yet. Add the first application.' : 'No applications match those filters.'}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr>{['Application', 'Vendor', 'Installed', 'Latest', 'Support status', 'Criticality', 'Support owner', 'Business owner', 'Last patched', 'Instances'].map(h => <th key={h} className={`${thCls} ${h === 'Instances' ? 'text-right' : ''}`}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {visible.map(a => (
                <tr key={a.id} onClick={() => open(a.id)} className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${ROW_TINT[a.support?.status] || ''}`}>
                  <td className={`${tdCls} font-semibold text-navy whitespace-nowrap`}>{a.name}</td>
                  <td className={`${tdCls} text-gray-500`}>{a.vendor || '—'}</td>
                  <td className={`${tdCls} whitespace-nowrap`}>{a.hosting === 'saas' ? <span className="text-gray-500">SaaS</span> : (a.installed_version || <span className="text-gray-400">Unknown</span>)}{a.instance_versions?.length > 1 && <span className="text-gray-400"> +{a.instance_versions.length - 1}</span>}</td>
                  <td className={`${tdCls} whitespace-nowrap`}>
                    {a.hosting === 'saas' ? <span className="text-gray-500">SaaS</span>
                      : a.support?.latest_version ? <><span className="font-semibold">{a.support.latest_version}</span> <VersionGap support={a.support} /></>
                      : <Badge cls="bg-gray-100 text-gray-600">{a.version_source === 'endoflife' ? 'Not checked yet' : 'No automatic check'}</Badge>}
                  </td>
                  <td className={tdCls}><SupportBadge support={a.support} /></td>
                  <td className={tdCls}><Badge cls={CRIT_BADGE[a.criticality]}>{CRITICALITY_LABELS[a.criticality]}</Badge></td>
                  <td className={`${tdCls} whitespace-nowrap`}><Owner name={a.support_owner_name} personId={a.support_owner_id} /></td>
                  <td className={`${tdCls} whitespace-nowrap`}><Owner name={a.business_owner_name} personId={a.business_owner_id} /></td>
                  <td className={`${tdCls} text-gray-500 whitespace-nowrap`}>{a.last_patched_on ? formatDate(a.last_patched_on) : (a.hosting === 'saas' ? 'n/a' : 'Never logged')}</td>
                  <td className={`${tdCls} text-right`}>{a.instance_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex flex-wrap gap-4 text-[8px] text-gray-500 items-center">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-100 border border-red-300" />Past end of life: no security fixes for the version we run</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-100 border border-amber-300" />Support ends within {WARN_DAYS} days</span>
        <span className="inline-flex items-center gap-1"><Badge cls="bg-gray-100 text-gray-600">No automatic check</Badge>Latest version and end-of-life dates are entered by hand</span>
      </div>

      {adding && <ApplicationFormDialog people={people} onClose={() => setAdding(false)} onSaved={(saved) => { setAdding(false); load(); if (saved?.id) open(saved.id); }} />}
      {tour.open && <FeatureTour steps={applicationCatalogueTourSteps} onFinish={tour.finish} onSkip={tour.skip} />}
    </div>
  );
}
